'use client'

import { useState, useMemo } from "react"
import { useQuery } from "@tanstack/react-query"
import { usePathname } from "next/navigation"
import { Moon, Sun } from "lucide-react"
import { useAuth } from "@/contexts/auth-context"
import { useTheme } from "@/hooks/use-theme"
import { getMe } from "@/lib/api/me"
import { Sidebar, BottomNav } from "@/components/layout/sidebar"
import { Breadcrumbs, type BreadcrumbItem } from "@/components/layout/Breadcrumbs"
import { UsuarioModal } from "@/components/config/usuario-modal"
import { ProtectedRoute } from "@/components/auth/protected-route"
import { OfflineBanner } from "@/components/layout/offline-banner"
import ErrorBoundary from "@/components/ui/error-boundary"
import { UserAvatar } from "@/components/layout/user-avatar"

export default function ProtectedLayout({
  children,
}: {
  children: React.ReactNode
}) {
  const pathname = usePathname()
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [profileOpen, setProfileOpen] = useState(false)

  const { user } = useAuth()
  const { theme, toggle: toggleTheme } = useTheme()

  // Simple breadcrumb from pathname: /protected/pacientes -> [{ label: "Pacientes", href: "/protected/pacientes" }]
  const breadcrumbs = useMemo<BreadcrumbItem[]>(() => {
    const parts = pathname?.split("/").filter(Boolean) ?? []
    // Skip "protected" prefix
    if (parts.length <= 1) return []
    const last = parts[parts.length - 1]
    // 2026-09-30 (auditoría visual): segmentos dinámicos (DNI, UUIDs) no son
    // etiquetas legibles — mostrar el padre capitalizado (p.ej. "Pacientes").
    const esDinamico = /^\d+$/.test(last) || /^[0-9a-f-]{36}$/i.test(last)
    const labelSource = esDinamico && parts.length >= 2 ? parts[parts.length - 2] : last
    const label = labelSource.charAt(0).toUpperCase() + labelSource.slice(1)
    return [{ label, href: esDinamico ? `/${parts.slice(0, -1).join("/")}` : pathname }]
  }, [pathname])
  const { data: meData } = useQuery({
    queryKey: ["me"],
    queryFn: getMe,
    enabled: !!user,
    staleTime: 1000 * 60 * 5,
  })

  const selfInitialData = meData ? {
    id: meData.id,
    username: meData.username,
    first_name: meData.first_name,
    last_name: meData.last_name,
    email: meData.email,
    rol: meData.rol,
    color_agenda: "#6366f1",
    activo: true,
    foto_url: meData.foto_url,
    modulos_habilitados: meData.modulos_habilitados,
  } : undefined

  return (
    <ProtectedRoute>
      <OfflineBanner />
      <div className="flex min-h-screen">

        {/* Mobile overlay — only shown when sidebar drawer is open */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-sm"
            onClick={() => setSidebarOpen(false)}
            aria-hidden
          />
        )}

        <Sidebar
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          onEditProfile={() => setProfileOpen(true)}
          meData={meData}
        />

        {selfInitialData && (
          <UsuarioModal
            open={profileOpen}
            onOpenChange={setProfileOpen}
            mode="edit"
            initialData={selfInitialData}
            selfEdit
          />
        )}

        <main className="flex-1 md:ml-56 min-h-screen overflow-x-hidden">
          {/* Mobile top bar — shows the app name on mobile */}
          <div className="md:hidden sticky top-0 z-30 flex items-center justify-between gap-3 px-4 h-14 bg-background/95 backdrop-blur-sm border-b border-border/60">
            <span className="text-sm font-bold tracking-tight uppercase">Aden CRM</span>
            <div className="flex items-center gap-1">
              <button
                onClick={toggleTheme}
                aria-label={theme === "dark" ? "Cambiar a modo claro" : "Cambiar a modo oscuro"}
                className="flex items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
              >
                {theme === "dark"
                  ? <Sun className="size-4" aria-hidden />
                  : <Moon className="size-4" aria-hidden />}
              </button>
              <button
                onClick={() => setProfileOpen(true)}
                aria-label="Editar perfil"
                className="flex items-center justify-center w-9 h-9 rounded-full hover:ring-2 hover:ring-ring transition-all"
              >
                <UserAvatar
                  nombre={meData?.nombre ?? user?.nombre ?? ""}
                  fotoUrl={meData?.foto_url}
                  size="sm"
                />
              </button>
            </div>
          </div>

          {/* Desktop spacer */}
          <div className="hidden md:block h-8" />

          {/* Breadcrumbs — desktop only */}
          <div className="max-w-6xl mx-auto px-4 md:px-8 pt-2">
            <Breadcrumbs items={breadcrumbs} />
          </div>

          {/* pb-16 accounts for the BottomNav height on mobile; removed on md+ */}
          <div className="max-w-6xl mx-auto px-4 md:px-8 py-4 md:py-8 pb-16 md:pb-8 text-foreground">
            <ErrorBoundary>
              {children}
            </ErrorBoundary>
          </div>
        </main>

        {/* Fixed bottom nav — mobile only */}
        <BottomNav />
      </div>
    </ProtectedRoute>
  )
}
